/**
 * components/tabs/UpstoxWatchlistRules.jsx
 *
 * upstox-051 — Settings → Upstox → Watchlists: the Risk Terminal watchlist's
 * AUTOMATIC lists, edited by the trader (never through code). One card per
 * list, in list order: name, underlying (offered from the instrument master),
 * strikes each side of ATM (1–20), expiry (nearest / next / monthly, with
 * the date it resolves to today), calls / puts / both, re-centre threshold
 * (1–10 listed strikes). SAVE and CANCEL per card; DELETE asks first; ADD
 * AUTOMATIC LIST (at most 6). Every rule is checked by the server; a refusal
 * shows in place. Saving re-centres the list at once (server side).
 *
 * The FIXED list is edited on the Risk Terminal screen, not here.
 *
 * `api` (tests / the dev harness pass a fake): { rules(), create(body),
 * update(id, body), remove(id) } → Promise<{ ok, status, data, message }>.
 */

import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function call(path, method = 'GET', body) {
  try {
    const r = await fetch(`${API}${path}`, {
      method, credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) return { ok: true, status: r.status, data: d };
    const det = d.detail;
    const message = typeof det === 'string' ? det
      : Array.isArray(det) ? det.map(e => `${(e.loc || []).slice(-1)[0]}: ${e.msg}`).join('; ')
      : (det && det.message) || `HTTP ${r.status}`;
    return { ok: false, status: r.status, data: d, message };
  } catch (e) {
    return { ok: false, status: 0, message: 'Network error — try again' };
  }
}

export const realRulesApi = {
  rules: () => call('/upstox/watchlist/rules'),
  create: body => call('/upstox/watchlist/rules', 'POST', body),
  update: (id, body) => call(`/upstox/watchlist/${id}/rule`, 'POST', body),
  remove: id => call(`/upstox/watchlist/${id}/delete`, 'POST', {}),
};

const DEFAULTS = { strikes_each_side: 10, expiry: 'nearest', sides: 'both', recentre_strikes: 3 };
const EXPIRY_LABEL = { nearest: 'Nearest', next: 'Next', monthly: 'Monthly' };
const SIDES_LABEL = { both: 'Calls and puts', CE: 'Calls only', PE: 'Puts only' };
const DESIGN_SIDES = { both: 'Calls & puts', CE: 'Calls only', PE: 'Puts only' };      // the handoff's wording

const label = { fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 12 };
const note = { fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 6 };
const field = { background: 'var(--surface-2)', border: '.5px solid var(--hair)', borderRadius: 6, color: 'var(--ink)', fontSize: 11, padding: '6px 9px', outline: 'none', fontFamily: 'var(--font-ui)', boxSizing: 'border-box' };
const btn = (kind, disabled) => ({
  fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '6px 13px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
  ...(kind === 'primary' ? { background: 'var(--amber-ink)', color: '#fff', border: 'none' }
    : kind === 'danger' ? { background: 'transparent', color: 'var(--down-field-ink)', border: '.5px solid var(--down-field-ink)' }
    : { background: 'transparent', color: 'var(--ink-2)', border: '.5px solid var(--hair)' }),
});

function fmtDay(iso) {
  if (!iso) return 'none listed';
  const d = new Date(iso + 'T00:00:00+05:30');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
}

function toForm(l) {
  const r = l.rule || {};
  return { id: l.id ?? null, name: l.name || '', underlying: r.underlying || '', strikes_each_side: String(r.strikes_each_side ?? DEFAULTS.strikes_each_side),
    expiry: r.expiry || DEFAULTS.expiry, sides: r.sides || DEFAULTS.sides, recentre_strikes: String(r.recentre_strikes ?? DEFAULTS.recentre_strikes) };
}

function Row({ name, children, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-2)', width: 170, flex: '0 0 170px' }}>{name}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{hint}</div>}
    </div>
  );
}

function Card({ list, data, api, onSaved, onCancelNew, initial, design }) {
  const [form, setForm] = useState(() => ({ ...toForm(list), ...((initial && initial.form) || {}) }));
  const [err, setErr] = useState((initial && initial.err) || '');
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(!!(initial && initial.confirmDelete));
  const isNew = list.id == null;
  const offer = (data.underlyings || []).find(u => u.underlying === form.underlying);
  const resolves = offer ? offer.expiries[form.expiry] : null;
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErr(''); };

  const save = async () => {
    const body = { name: form.name, underlying: form.underlying, expiry: form.expiry, sides: form.sides,
      strikes_each_side: Number(form.strikes_each_side), recentre_strikes: Number(form.recentre_strikes) };
    for (const k of ['strikes_each_side', 'recentre_strikes']) {
      if (!Number.isInteger(body[k])) { setErr(`Enter a whole number for ${k === 'strikes_each_side' ? 'strikes each side' : 'the re-centre threshold'}.`); return; }
    }
    setBusy(true); setErr('');
    const r = isNew ? await api.create(body) : await api.update(list.id, body);
    setBusy(false);
    if (r.ok) onSaved(); else setErr(r.message);
  };
  const remove = async () => {
    setBusy(true);
    const r = await api.remove(list.id);
    setBusy(false);
    if (r.ok) onSaved(); else { setErr(r.message); setConfirmDel(false); }
  };

  if (design) {
    // upstox-073 — the Claude Design handoff's inline edit panel: a three-column grid, labels above the fields
    const lab = { display: 'flex', flexDirection: 'column', gap: 5 };
    const lt = { fontSize: 12, color: 'var(--ink-2)' };
    const wrap = { display: 'flex', alignItems: 'center', height: 32, border: '1px solid var(--hair-strong)', borderRadius: 6, background: 'var(--surface)' };
    const num = { flex: 1, minWidth: 0, border: 'none', outline: 'none', padding: '0 10px', fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 13, background: 'transparent', color: 'var(--ink)' };
    return (
      <div data-rule-card={isNew ? 'new' : list.id} style={{ margin: '0 16px 14px', padding: 16, borderRadius: 8, background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="sx2-grid3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          <label style={lab}><span style={lt}>Name</span><input className="sx2-input" value={form.name} onChange={e => set('name', e.target.value)} maxLength={60} /></label>
          <label style={lab}><span style={lt}>Underlying</span>
            <select className="sx2-input" value={form.underlying} onChange={e => set('underlying', e.target.value)}>
              {!offer && <option value={form.underlying}>{form.underlying || '—'} (no options listed)</option>}
              {(data.underlyings || []).map(u => <option key={u.underlying} value={u.underlying}>{u.underlying}</option>)}
            </select></label>
          <label style={lab}><span style={lt}>Expiry</span>
            <select className="sx2-input" value={form.expiry} onChange={e => set('expiry', e.target.value)}>
              {Object.entries(EXPIRY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}{offer ? ' → ' + fmtDay(offer.expiries[k]) : ''}</option>)}
            </select></label>
          <label style={lab}><span style={lt}>Options</span>
            <select className="sx2-input" value={form.sides} onChange={e => set('sides', e.target.value)}>
              {Object.entries(SIDES_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label style={lab}><span style={lt}>Strikes each side of ATM</span>
            <div className="sx2-wrap" style={wrap}><input value={form.strikes_each_side} inputMode="numeric" onChange={e => set('strikes_each_side', e.target.value)} style={num} />
              <span style={{ padding: '0 10px', fontSize: 11, color: 'var(--ink-2)' }}>1–20</span></div></label>
          <label style={lab}><span style={lt}>Re-centre when ATM moves</span>
            <div className="sx2-wrap" style={wrap}><input value={form.recentre_strikes} inputMode="numeric" onChange={e => set('recentre_strikes', e.target.value)} style={num} />
              <span style={{ padding: '0 10px', fontSize: 11, color: 'var(--ink-2)' }}>strikes · 1–10</span></div></label>
        </div>
        {err && <div data-refusal style={{ fontSize: 12, color: 'var(--down-field-ink)' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="sx2-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          <button type="button" className="sx2-ghost" disabled={busy} onClick={() => (isNew ? onCancelNew() : (setForm(toForm(list)), setErr(''), onCancelNew()))}>Cancel</button>
          <span style={{ flex: 1 }} />
          {!isNew && !confirmDel && <button type="button" className="sx2-ghost" style={{ color: 'var(--down-field-ink)' }} disabled={busy} onClick={() => setConfirmDel(true)}>Delete list</button>}
          {!isNew && confirmDel && (
            <>
              <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Delete list {list.name}?</span>
              <button type="button" className="sx2-ghost" style={{ color: 'var(--down-field-ink)' }} disabled={busy} onClick={remove}>Delete</button>
              <button type="button" className="sx2-ghost" disabled={busy} onClick={() => setConfirmDel(false)}>Keep</button>
            </>
          )}
        </div>
      </div>
    );
  }
  return (
    <div data-rule-card={isNew ? 'new' : list.id} style={{ border: '.5px solid var(--hair)', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: 'var(--surface)', maxWidth: 560 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 10 }}>{isNew ? 'New automatic list' : list.name}</div>
      <div>
      <Row name="Name"><input value={form.name} onChange={e => set('name', e.target.value)} style={{ ...field, width: 200 }} maxLength={60} /></Row>
      <Row name="Underlying">
        <select value={form.underlying} onChange={e => set('underlying', e.target.value)} style={{ ...field, width: 200 }}>
          {!offer && <option value={form.underlying}>{form.underlying || '—'} (no options listed)</option>}
          {(data.underlyings || []).map(u => <option key={u.underlying} value={u.underlying}>{u.underlying}</option>)}
        </select>
      </Row>
      <Row name="Strikes each side of ATM" hint="1–20">
        <input value={form.strikes_each_side} inputMode="numeric" onChange={e => set('strikes_each_side', e.target.value)} style={{ ...field, width: 70,  }} />
      </Row>
      <Row name="Expiry" hint={'→ ' + fmtDay(resolves) + ' today'}>
        <select value={form.expiry} onChange={e => set('expiry', e.target.value)} style={{ ...field, width: 200 }}>
          {Object.entries(EXPIRY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}{offer ? ' · ' + fmtDay(offer.expiries[k]) : ''}</option>)}
        </select>
      </Row>
      <Row name="Calls, puts or both">
        <select value={form.sides} onChange={e => set('sides', e.target.value)} style={{ ...field, width: 200 }}>
          {Object.entries(SIDES_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Row>
      <Row name="Re-centre when ATM moves" hint="listed strikes, 1–10">
        <input value={form.recentre_strikes} inputMode="numeric" onChange={e => set('recentre_strikes', e.target.value)} style={{ ...field, width: 70,  }} />
      </Row>
      </div>
      {err && (
        <div data-refusal style={{ margin: '8px 0 2px', fontSize: 11, color: 'var(--down-field-ink)', background: 'rgba(193,48,26,.06)', border: '.5px dashed var(--down-field-ink)', borderRadius: 4, padding: '6px 9px' }}>{err}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={save} disabled={busy} style={btn('primary', busy)}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={() => (isNew ? onCancelNew() : (setForm(toForm(list)), setErr('')))} disabled={busy} style={btn('ghost', busy)}>Cancel</button>
        <div style={{ flex: 1 }} />
        {!isNew && !confirmDel && <button type="button" onClick={() => setConfirmDel(true)} disabled={busy} style={btn('danger', busy)}>Delete</button>}
        {!isNew && confirmDel && (
          <>
            <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>Delete list {list.name}?</span>
            <button type="button" onClick={remove} disabled={busy} style={btn('danger', busy)}>Delete</button>
            <button type="button" onClick={() => setConfirmDel(false)} disabled={busy} style={btn('ghost', busy)}>Keep</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function UpstoxWatchlistRules({ api = realRulesApi, initial, design = false }) {
  const [data, setData] = useState((initial && initial.data) || null);
  const [loadErr, setLoadErr] = useState('');
  const [adding, setAdding] = useState(!!(initial && initial.adding));
  const [editId, setEditId] = useState((initial && initial.editId) || null);      // upstox-073: the row being edited
  const [gen, setGen] = useState(0);

  const load = async () => {
    const r = await api.rules();
    if (r.ok) { setData(r.data); setLoadErr(''); setGen(g => g + 1); } else setLoadErr('Could not load the watchlists — ' + r.message);
  };
  useEffect(() => { if (!(initial && initial.data)) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lists = (data && data.lists) || [];
  const max = (data && data.max_lists) || 6;
  const first = data && data.underlyings && data.underlyings[0] ? data.underlyings[0].underlying : '';
  const newList = { id: null, name: '', rule: { underlying: first, ...DEFAULTS } };

  if (design) {
    // upstox-073 — the Claude Design handoff: a table, an inline edit panel under the row being edited
    const grid = '1.2fr .9fr 1.5fr 1fr .7fr .9fr 60px';
    const cols = { display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center' };
    return (
      <div data-watchlists style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loadErr && <div style={{ fontSize: 12, color: 'var(--down-field-ink)' }}>{loadErr}</div>}
        <div style={{ border: '1px solid var(--hair)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
          <div className="sx2-scroll">
            <div style={{ minWidth: 640 }}>
              <div style={{ ...cols, padding: '10px 16px', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-2)', background: 'var(--surface-2)', borderBottom: '1px solid var(--hair)' }}>
                <span>Name</span><span>Underlying</span><span>Expiry</span><span>Options</span><span>Strikes</span><span>Re-centre</span><span style={{ position: 'sticky', right: 0, background: 'var(--surface-2)' }} />
              </div>
              {data && lists.map((l, i) => {
                const r = l.rule || {}, editing = editId === l.id;
                return (
                  <div key={l.id} data-watchlist-row={l.id} style={{ borderTop: i ? '1px solid var(--hair)' : 'none' }}>
                    <div style={{ ...cols, padding: '11px 16px', fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{l.name}</span>
                      <span>{r.underlying}</span>
                      <span style={{ color: 'var(--ink-2)' }}>{EXPIRY_LABEL[r.expiry] || r.expiry}{l.resolved_expiry ? ' → ' + fmtDay(l.resolved_expiry) : ''}</span>
                      <span style={{ color: 'var(--ink-2)' }}>{DESIGN_SIDES[r.sides] || r.sides}</span>
                      <span style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 12.5 }}>±{r.strikes_each_side}</span>
                      <span style={{ color: 'var(--ink-2)' }}>{r.recentre_strikes} strikes</span>
                      {/* sticky: the Edit button stays in view when a narrow window scrolls the table sideways */}
                      <span style={{ position: 'sticky', right: 0, background: 'var(--surface)', textAlign: 'right' }}>
                        <button type="button" className="sx2-link" data-edit-list={l.id} style={{ fontSize: 13, fontWeight: 500, textDecoration: 'none' }}
                          aria-expanded={editing} onClick={() => { setAdding(false); setEditId(editing ? null : l.id); }}>{editing ? 'Close' : 'Edit'}</button>
                      </span>
                    </div>
                    {editing && (
                      <Card key={l.id + ':' + gen} list={l} data={data} api={api} design
                        onSaved={() => { setEditId(null); load(); }} onCancelNew={() => setEditId(null)}
                        initial={initial && initial.cards && initial.cards[i]} />
                    )}
                  </div>
                );
              })}
              {data && !lists.length && <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--ink-2)' }}>No automatic lists.</div>}
              {!data && !loadErr && <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--ink-2)' }}>Loading…</div>}
            </div>
          </div>
          {data && adding && (
            <div style={{ borderTop: '1px solid var(--hair)', paddingTop: 14 }}>
              <Card key={'new:' + gen} list={newList} data={data} api={api} design
                onSaved={() => { setAdding(false); load(); }} onCancelNew={() => setAdding(false)} initial={initial && initial.newCard} />
            </div>
          )}
        </div>
        {data && !adding && (
          <div>
            <button type="button" disabled={lists.length >= max} onClick={() => { setEditId(null); setAdding(true); }}
              title={lists.length >= max ? `At most ${max} automatic lists` : 'A new list with the default rule'}
              style={{ height: 32, padding: '0 12px', border: '1px dashed var(--hair-strong)', background: 'transparent', borderRadius: 6, fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', cursor: lists.length >= max ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
              + Add automatic list {lists.length >= max ? `(at most ${max})` : ''}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '.5px solid var(--hair)' }}>
      <div style={label}>Watchlists</div>
      <div style={note}>
        The Risk Terminal's automatic lists: an option chain around the money, re-centred as the market moves. Saving re-centres the list at once.
        The fixed list is edited on the terminal itself.
      </div>
      {loadErr && <div style={{ ...note, color: 'var(--down-field-ink)' }}>{loadErr}</div>}
      {data && lists.map((l, i) => (
        <Card key={l.id + ':' + gen} list={l} data={data} api={api} onSaved={load} onCancelNew={() => {}}
          initial={initial && initial.cards && initial.cards[i]} />
      ))}
      {data && adding && (
        <Card key={'new:' + gen} list={newList} data={data} api={api}
          onSaved={() => { setAdding(false); load(); }} onCancelNew={() => setAdding(false)}
          initial={initial && initial.newCard} />
      )}
      {data && !adding && (
        <button type="button" onClick={() => setAdding(true)} disabled={lists.length >= max} style={btn('ghost', lists.length >= max)}
          title={lists.length >= max ? `At most ${max} automatic lists` : 'A new list with the default rule'}>
          + Add automatic list {lists.length >= max ? `(at most ${max})` : ''}
        </button>
      )}
    </div>
  );
}
